import { FC, useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import { CertHouseholdData, CertMember } from 'src/types/Certification';
import HouseholdCompositionLoadingSkeleton from './HouseholdCompositionLoadingSkeleton';
import HouseholdCompositionEmptyState from './HouseholdCompositionEmptyState';
import HouseholdCompositionMemberCard from '../Household/HouseholdMemberCard';
import HouseholdCompositionToolbar from './HouseholdCompositionToolbar';
import { fadeStyle } from 'src/components/NewRenterInterface/constants';
import { getDuplicateContactMemberIds } from '../Household/householdContactUtils';

interface HouseholdCompositionMembersListProps {
  household: CertHouseholdData;
  onAddMember: () => void;
  onHelp?: () => void;
  onMemberClick?: (member: CertMember) => void;
  onEditMember?: (member: CertMember) => void;
  onDeleteMember?: (member: CertMember) => void;
  /** When false, toolbar (Add Member) is hidden; edit/details/action-required only on current user's card */
  canManage?: boolean;
  /** When canManage is false, edit/details and action required are only shown for the member with this user id */
  currentUserId?: number;
}

const HouseholdCompositionMembersList: FC<
  HouseholdCompositionMembersListProps
> = ({
  household,
  onAddMember,
  onHelp,
  onMemberClick,
  onEditMember,
  onDeleteMember,
  canManage = true,
  currentUserId,
}) => {
  const highlights = useSelector(
    (state: RootState) => state.newRenterInterface.highlights
  );

  const members = useMemo(() => {
    if (!household.cert_members) {
      return null;
    }
    const list = household.cert_members.slice();
    return list.sort((a, b) => {
      if (!canManage && currentUserId != null) {
        const aIsCurrentUser = a.user_id === currentUserId;
        const bIsCurrentUser = b.user_id === currentUserId;
        if (aIsCurrentUser && !bIsCurrentUser) {
          return -1;
        }
        if (!aIsCurrentUser && bIsCurrentUser) {
          return 1;
        }
      }

      const aIsHelper = a.is_helper;
      const bIsHelper = b.is_helper;

      if (aIsHelper && !bIsHelper) {
        return 1;
      }
      if (!aIsHelper && bIsHelper) {
        return -1;
      }

      const wA = a.member_type_weight ?? 0;
      const wB = b.member_type_weight ?? 0;
      return wA - wB;
    });
  }, [household.cert_members, canManage, currentUserId]);

  const duplicateMemberIds = useMemo(
    () => getDuplicateContactMemberIds(household.cert_members ?? []),
    [household.cert_members]
  );

  if (members === null) {
    return <HouseholdCompositionLoadingSkeleton />;
  }

  if (members.length === 0) {
    return (
      <>
        <HouseholdCompositionToolbar
          onAddMember={onAddMember}
          onHelp={onHelp}
          canManage={canManage}
        />
        <HouseholdCompositionEmptyState />
      </>
    );
  }

  return (
    <Box sx={fadeStyle}>
      <HouseholdCompositionToolbar
        onAddMember={onAddMember}
        onHelp={onHelp}
        canManage={canManage}
        memberCount={members.length}
      />
      <Stack gap={2}>
        {members.map((member) => (
          <HouseholdCompositionMemberCard
            key={member.cert_member_id}
            member={member}
            isRecentlyAdded={highlights.includes(member.cert_member_id)}
            hasDuplicateContact={duplicateMemberIds.has(member.cert_member_id)}
            canManage={canManage}
            currentUserId={currentUserId}
            onClick={onMemberClick}
            onEdit={onEditMember}
            onDelete={onDeleteMember}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default HouseholdCompositionMembersList;
